import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Lock, Clock } from "lucide-react";
import { api, formatDuration } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Loader } from "../components/ProtectedRoute";
import { MoodBadge, TagChip } from "../components/MoodTagEditor";
import { MOODS } from "../lib/moods";
import { ExportPdfButton } from "../components/ExportPdfButton";

const EntryCard = ({ e, lang, i }) => (
  <Link
    to={`/journal/${e.id}`}
    data-testid={`journal-entry-card-${e.id}`}
    className="gold-card p-6 block group fade-up"
    style={{ animationDelay: `${i * 60}ms` }}
  >
    <div className="flex items-center justify-between text-xs text-[#8E9BB4] mb-4">
      <span>{new Date(e.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
      <span className="flex items-center gap-2">
        <MoodBadge mood={e.mood} testid={`journal-entry-mood-${e.id}`} />
        <span className="uppercase tracking-wider border border-[#E5C158]/30 text-[#E5C158] rounded-full px-2 py-0.5">{e.language}</span>
        <Lock size={12} className="text-[#E5C158]/70" />
      </span>
    </div>
    <h3 className="font-serif text-2xl text-[#F4F6FA] group-hover:text-[#E5C158] transition-colors line-clamp-1">{e.title}</h3>
    <p className="mt-2 text-sm text-[#8E9BB4] line-clamp-2 leading-relaxed">{e.transcript}</p>
    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-[#8E9BB4]">
      <span className="flex items-center gap-1.5 mr-1"><Clock size={12} /> {formatDuration(e.duration)}</span>
      {e.tags.map((tag) => <TagChip key={tag} tag={tag} testid={`journal-entry-tag-${e.id}-${tag}`} />)}
    </div>
  </Link>
);

const Pill = ({ active, onClick, testid, children, color }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors duration-200"
    style={active ? { background: `${color || "#E5C158"}22`, borderColor: color || "#E5C158", color: color || "#E5C158" } : { borderColor: "rgba(255,255,255,0.1)", color: "#C2C9DB" }}
  >
    {children}
  </button>
);

export default function Journal() {
  const { t, lang } = useI18n();
  const [entries, setEntries] = useState(null);
  const [q, setQ] = useState("");
  const [mood, setMood] = useState(null);
  const [tag, setTag] = useState(null);

  useEffect(() => {
    api.get("/entries").then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, []);

  const allTags = useMemo(() => [...new Set((entries || []).flatMap((e) => e.tags))].sort(), [entries]);
  const usedMoods = useMemo(() => MOODS.filter((m) => (entries || []).some((e) => e.mood === m.key)), [entries]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (entries || []).filter((e) =>
      (!mood || e.mood === mood) &&
      (!tag || e.tags.includes(tag)) &&
      (!s || e.title.toLowerCase().includes(s) || e.transcript.toLowerCase().includes(s) || e.tags.some((x) => x.includes(s)))
    );
  }, [entries, q, mood, tag]);

  if (entries === null) return <Loader />;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
        <div>
          <p className="label-gold mb-3">{t("j_kicker")}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F6FA] font-semibold">{t("j_title")}</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <ExportPdfButton />
          <Link to="/record" data-testid="journal-new-entry-btn" className="btn-gold px-5 py-3 rounded-full inline-flex items-center gap-2 w-fit">
            <Plus size={16} /> {t("j_new")}
          </Link>
        </div>
      </div>
      <div className="relative mb-6 max-w-lg">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8E9BB4]" />
        <input data-testid="journal-search-input" className="input-navy" style={{ paddingLeft: "2.75rem" }} placeholder={t("j_search")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {usedMoods.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="journal-mood-filter">
          <span className="label-gold mr-2">{t("j_filter_mood")}</span>
          <Pill testid="journal-mood-filter-all" active={!mood} onClick={() => setMood(null)}>{t("j_all")}</Pill>
          {usedMoods.map(({ key, icon: Icon, color }) => (
            <Pill key={key} testid={`journal-mood-filter-${key}`} active={mood === key} color={color} onClick={() => setMood(mood === key ? null : key)}>
              <Icon size={13} /> {t(`mood_${key}`)}
            </Pill>
          ))}
        </div>
      )}
      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="journal-tag-filter">
          <span className="label-gold mr-2">{t("j_filter_tags")}</span>
          {allTags.map((x) => (
            <TagChip key={x} tag={x} testid={`journal-tag-filter-${x}`} active={tag === x} onClick={() => setTag(tag === x ? null : x)} />
          ))}
        </div>
      )}
      {entries.length === 0 ? (
        <div className="glass-card p-12 text-center" data-testid="journal-empty-state">
          <p className="font-serif text-2xl text-[#F4F6FA] mb-6">{t("j_empty")}</p>
          <Link to="/record" data-testid="journal-empty-cta" className="btn-gold px-6 py-3 rounded-full inline-flex items-center gap-2">{t("j_empty_cta")}</Link>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-[#8E9BB4]" data-testid="journal-no-match">{t("j_no_match")}</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-5" data-testid="journal-entries-list">
          {filtered.map((e, i) => <EntryCard key={e.id} e={e} lang={lang} i={i} />)}
        </div>
      )}
    </div>
  );
}
